// Wave 20.49cp — Share-card service (web).
// Wave 20.49cr — 2×3 metric grid, peak pins, de-duplicated copy.
// Wave 20.49cs — Wind still over the map, large numbered pins drawn
// on the canvas (above the wind), deterministic map frame. This
// renderer is now the single source of truth for BOTH platforms: the
// mobile app renders it through /share-card-embed in a WebView.
//
// Tier-1 social sharing: no Meta OAuth, no Graph API. The card is
// drawn on a <canvas> at 1080×1920 (Instagram Stories' 9:16), then:
//   • mobile browsers: navigator.share({ files }) hands the PNG to
//     the OS sheet — Instagram, Facebook, Messages, …
//   • desktop: download the PNG + copy the caption.
//
// Every number goes through useUnitSystem's formatters (unit rule).

import { colors } from '../theme';
import {
  fmtDistance,
  fmtElevation,
  fmtPace,
  fmtSpeed,
  type UnitSystem,
} from './useUnitSystem';
import { encodePolyline, decimate, type LatLng } from './staticRoute';
import { fitMapFrame, staticImageUrlForFrame, type MapFrame } from './mapFrame';
import { fetchWindField, renderWindStill } from './windStill';

export const CARD_W = 1080;
export const CARD_H = 1920;

const API = process.env.REACT_APP_API || 'https://api.aero.chat';
const TOKEN_KEY = process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token';
const MAPBOX_TOKEN =
  process.env.REACT_APP_MAPBOX_TOKEN ||
  process.env.REACT_APP_MAPBOX_ACCESS_TOKEN ||
  'pk.eyJ1IjoiYWVyby1jaGF0IiwiYSI6ImNtY3J4bnp0MTBjeTYybm42bzVjbWtsdmkifQ.rbgswhST93lMRY-CzQZ05Q';

/** The slice of an activity the card needs — ActivityDetail's type
 *  satisfies this structurally. */
export interface ShareableActivity {
  type: 'indoor_aero' | 'outdoor_cycling' | 'outdoor_running';
  title: string | null;
  startedAt: string;
  endedAt: string | null;
  distanceM: number | null;
  elevationGainM: number | null;
  avgSpeedMps: number | null;
  maxSpeedMps?: number | null;
  gpsTrack: Array<(LatLng & { t: number } & Record<string, unknown>) | null> | null;
  /** Wave 20.42 rollup: avg_headwind_mps is Σ(v·cosΔ)·dist ÷ Σdist along
   *  the route (positive = headwind). Aero intensity derives from it. */
  weatherSummary?: {
    wind_speed_ms?: { avg: number | null; max?: number | null } | null;
    avg_headwind_mps?: number | null;
  } | null;
  /** Wave 20.49z accelerometer out-of-saddle intervals (cycling). */
  standingIntervals?: Array<{ startSec: number; endSec: number }> | null;
}

/** A peak/interval row (ActivityDetail's Interval satisfies this). */
export interface PeakLike {
  kind: string;
  value: number;
  unit: string;
  startSec: number;
  endSec: number;
}

export interface ShareCardModel {
  title: string;
  subtitle: string;
  /** Route points for the map frame (nulls = privacy breaks). */
  track: Array<LatLng | null>;
  /** Numbered pins at each peak's midpoint. */
  pins: Array<LatLng & { label: string }>;
  /** ISO time to sample the wind field at. */
  windAtIso: string;
  stats: Array<{ label: string; value: string; accent?: boolean }>;
  footnote: string | null;
  caption: string;
}

const PEAK_TILE_LABELS: Record<string, string> = {
  peak_pace: 'PEAK PACE',
  peak_power: 'PEAK POWER',
  peak_speed: 'PEAK SPEED',
  peak_gap: 'PEAK GAP',
  peak_vam: 'PEAK VAM',
};
const PEAK_LEGEND_LABELS: Record<string, string> = {
  peak_pace: 'Peak pace',
  peak_power: 'Peak power',
  peak_speed: 'Peak speed',
  peak_gap: 'Grade-adj pace',
  peak_vam: 'Climb (VAM)',
};

function fmtSecs(sec: number): string {
  const h = Math.floor(sec / 3600);
  const m = Math.round((sec % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function fmtPeakValue(p: PeakLike, units: UnitSystem): string {
  switch (p.unit) {
    case 'sec_per_m': return p.value > 0 ? fmtPace(1 / p.value, units) : '—';
    case 'mps': return fmtSpeed(p.value, units);
    case 'W': return `${Math.round(p.value)} W`;
    case 'm_per_h': return `${Math.round(p.value)} m/h`;
    case 'm': return fmtDistance(p.value, units);
    case 'sec': return fmtSecs(p.value);
    default: return `${Math.round(p.value)}`;
  }
}

/** "Short ride — 3.1 mi · 148 ft up" → "Short ride". */
function cleanTitle(raw: string): string {
  const i = raw.indexOf(' — ');
  if (i > 0 && /\d/.test(raw.slice(i + 3))) return raw.slice(0, i).trim();
  return raw.trim();
}

function pointAt(track: NonNullable<ShareableActivity['gpsTrack']>, sec: number): LatLng | null {
  let best: LatLng | null = null;
  let bestDt = Infinity;
  for (const p of track) {
    if (!p) continue;
    const dt = Math.abs(p.t - sec);
    if (dt < bestDt) { bestDt = dt; best = { lat: p.lat, lng: p.lng }; }
  }
  return best;
}

/** Aero intensity = headwind share of the wind the rider rode in,
 *  the same notion the route planner labels low/mid/high:
 *  avg_headwind ÷ avg_wind mapped from [-1, 1] → [0, 1], so a pure
 *  tailwind ride reads 0 %, crosswind 50 %, pure headwind 100 %.
 *  Null when the session has no wind rollup or the air was calm. */
export function aeroIntensityFrom(ws: ShareableActivity['weatherSummary']): number | null {
  const head = ws?.avg_headwind_mps;
  const wind = ws?.wind_speed_ms?.avg;
  if (head == null || wind == null || !Number.isFinite(head) || !Number.isFinite(wind) || wind < 0.5) return null;
  const ratio = Math.max(-1, Math.min(1, head / wind));
  return (ratio + 1) / 2;
}

/** "high · 71%" */
export function fmtAeroIntensity(x: number): string {
  const pct = Math.round(Math.max(0, Math.min(1, x)) * 100);
  const bucket = x < 0.34 ? 'low' : x < 0.67 ? 'mid' : 'high';
  return `${bucket} · ${pct}%`;
}

/** Total out-of-saddle seconds from the accelerometer intervals. */
export function standingSecFrom(iv: ShareableActivity['standingIntervals']): number {
  if (!Array.isArray(iv)) return 0;
  return iv.reduce((s, x) => s + Math.max(0, (x?.endSec ?? 0) - (x?.startSec ?? 0)), 0);
}

export function buildShareCardModel(
  a: ShareableActivity,
  units: UnitSystem,
  intervals: PeakLike[] = [],
): ShareCardModel {
  const isRun = a.type === 'outdoor_running';
  const activity = isRun ? 'run' : 'ride';
  const title = cleanTitle(a.title || '') || (isRun ? 'Run' : 'Ride');
  const when = a.startedAt ? new Date(a.startedAt) : null;
  const subtitle = when
    ? when.toLocaleString(undefined, {
        weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
      })
    : '';

  const track = (a.gpsTrack ?? []).map((p) => (p ? { lat: p.lat, lng: p.lng } : null));
  const peaks = intervals
    .filter((p) => p.kind !== 'peak_whole' && Number.isFinite(p.startSec) && Number.isFinite(p.endSec))
    .sort((x, y) => x.startSec - y.startSec)
    .slice(0, 9);
  const pins = peaks
    .map((p, i) => {
      const pt = pointAt(a.gpsTrack ?? [], (p.startSec + p.endSec) / 2);
      return pt ? { ...pt, label: String(i + 1) } : null;
    })
    .filter((m): m is LatLng & { label: string } => m != null);
  // Sample wind mid-ride so a 2 h ride doesn't show the start-line field.
  const midMs = a.endedAt && a.startedAt
    ? (new Date(a.startedAt).getTime() + new Date(a.endedAt).getTime()) / 2
    : new Date(a.startedAt).getTime();
  const windAtIso = new Date(midMs).toISOString();

  const distance = fmtDistance(a.distanceM ?? 0, units);
  const duration = a.endedAt ? fmtSecs(Math.max(0, (new Date(a.endedAt).getTime() - new Date(a.startedAt).getTime()) / 1000)) : null;
  const avgSpeed = a.avgSpeedMps != null && a.avgSpeedMps > 0
    ? (isRun ? fmtPace(a.avgSpeedMps, units) : fmtSpeed(a.avgSpeedMps, units))
    : null;
  const elev = a.elevationGainM != null ? fmtElevation(a.elevationGainM, units) : null;
  const maxSpeed = a.maxSpeedMps != null && a.maxSpeedMps > 0 ? fmtSpeed(a.maxSpeedMps, units) : null;
  const aeroX = aeroIntensityFrom(a.weatherSummary);
  const aero = aeroX != null ? fmtAeroIntensity(aeroX) : null;
  const standingSec = standingSecFrom(a.standingIntervals);
  const standing = standingSec >= 30 ? fmtSecs(standingSec) : null;

  const primaryKind = isRun ? 'peak_pace' : intervals.some((p) => p.kind === 'peak_power') ? 'peak_power' : 'peak_speed';
  const primaryPeak = peaks.find((p) => p.kind === primaryKind) ?? null;

  // Six tiles, in priority order.
  const stats: ShareCardModel['stats'] = [{ label: 'DISTANCE', value: distance, accent: true }];
  if (aero) stats.push({ label: 'AERO INTENSITY', value: aero, accent: true });
  if (duration) stats.push({ label: 'TIME', value: duration });
  if (avgSpeed) stats.push({ label: isRun ? 'AVG PACE' : 'AVG SPEED', value: avgSpeed });
  if (standing) stats.push({ label: 'STANDING', value: standing });
  if (elev) stats.push({ label: 'ELEVATION', value: elev });
  if (primaryPeak) stats.push({ label: PEAK_TILE_LABELS[primaryPeak.kind] ?? 'PEAK', value: fmtPeakValue(primaryPeak, units), accent: true });
  if (maxSpeed && !isRun) stats.push({ label: 'MAX SPEED', value: maxSpeed });
  const six = stats.slice(0, 6);

  const legend = peaks
    .map((p, i) => `${i + 1} ${PEAK_LEGEND_LABELS[p.kind] ?? p.kind.replace(/^peak_/, '')} ${fmtPeakValue(p, units)}`)
    .join(' · ');
  const footnote = legend || null;

  const capBits = [`${distance} ${activity}`];
  if (aero) capBits.push(`aero intensity ${aero}`);
  if (elev) capBits.push(`${elev} up`);
  if (primaryPeak) capBits.push(`${PEAK_LEGEND_LABELS[primaryPeak.kind]?.toLowerCase() ?? 'peak'} ${fmtPeakValue(primaryPeak, units)}`);
  const caption = `${title} — ${capBits.join(' · ')} · aero.chat`;

  return { title, subtitle, track, pins, windAtIso, stats: six, footnote, caption };
}

// ---------- canvas rendering ----------

function roundedRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function loadImage(url: string, timeoutMs = 8000): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    const t = window.setTimeout(() => resolve(null), timeoutMs);
    img.onload = () => { window.clearTimeout(t); resolve(img); };
    img.onerror = () => { window.clearTimeout(t); resolve(null); };
    img.src = url;
  });
}

function fitFont(ctx: CanvasRenderingContext2D, text: string, maxW: number, weight: number, px: number, family: string, minPx: number): string {
  let size = px;
  while (size > minPx) {
    ctx.font = `${weight} ${size}px ${family}`;
    if (ctx.measureText(text).width <= maxW) break;
    size -= 2;
  }
  return ctx.font;
}

function ellipsize(ctx: CanvasRenderingContext2D, text: string, maxW: number): string {
  if (ctx.measureText(text).width <= maxW) return text;
  let s = text;
  while (s.length > 1 && ctx.measureText(`${s}…`).width > maxW) s = s.slice(0, -1);
  return `${s}…`;
}

function wrapLines(ctx: CanvasRenderingContext2D, text: string, maxW: number, maxLines: number): string[] {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let cur = '';
  for (const w of words) {
    const next = cur ? `${cur} ${w}` : w;
    if (ctx.measureText(next).width <= maxW || !cur) {
      cur = next;
    } else {
      lines.push(cur);
      cur = w;
      if (lines.length === maxLines - 1) break;
    }
  }
  if (cur) lines.push(cur);
  if (lines.length > maxLines) lines.length = maxLines;
  if (lines.length === maxLines) {
    const used = lines.slice(0, -1).join(' ');
    const rest = used ? text.slice(used.length).trim() : text;
    lines[maxLines - 1] = ellipsize(ctx, rest || lines[maxLines - 1], maxW);
  }
  return lines;
}

const FONT = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';
const MONO = '"JetBrains Mono", ui-monospace, Menlo, monospace';

export interface DrawOptions {
  /** Bearer token for the wind field. Defaults to localStorage (web app). */
  jwt?: string | null;
  /** Skip the wind layer (offline / fast preview). */
  noWind?: boolean;
}

/** Draw the card onto `canvas` (sized to CARD_W×CARD_H). Resolves
 *  once map + wind are in (or gave up). */
export async function drawShareCard(canvas: HTMLCanvasElement, m: ShareCardModel, opts: DrawOptions = {}): Promise<void> {
  canvas.width = CARD_W;
  canvas.height = CARD_H;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const PAD = 66;
  const W = CARD_W;
  const innerW = W - PAD * 2;
  const jwt = opts.jwt !== undefined ? opts.jwt : (typeof localStorage !== 'undefined' ? localStorage.getItem(TOKEN_KEY) : null);

  ctx.fillStyle = colors.bg;
  ctx.fillRect(0, 0, CARD_W, CARD_H);

  // Header
  ctx.textBaseline = 'top';
  ctx.fillStyle = colors.brand;
  ctx.font = `800 33px ${MONO}`;
  ctx.fillText('AERO.CHAT', PAD, 80);
  ctx.fillStyle = colors.text;
  ctx.font = `900 72px ${FONT}`;
  const titleLines = wrapLines(ctx, m.title, innerW, 2);
  titleLines.forEach((line, i) => ctx.fillText(line, PAD, 126 + i * 84));
  const afterTitle = 126 + titleLines.length * 84 + 6;
  ctx.fillStyle = colors.textMuted;
  ctx.font = `600 36px ${FONT}`;
  ctx.fillText(ellipsize(ctx, m.subtitle, innerW), PAD, afterTitle);

  // Map panel (4:3). The static image is requested at the panel's
  // CSS size (max 1280) @2x; the frame's projection is in CSS px.
  const mapY = afterTitle + 70;
  const mapW = innerW;
  const mapH = Math.round(mapW * 3 / 4);
  roundedRect(ctx, PAD, mapY, mapW, mapH, 48);
  ctx.save();
  ctx.clip();
  ctx.fillStyle = '#0e0e12';
  ctx.fillRect(PAD, mapY, mapW, mapH);

  const frame: MapFrame | null = fitMapFrame(m.track, mapW, mapH);
  if (frame) {
    // Route overlay tokens (same encoder as the Feed sketch).
    const strokeHex = (colors.brand || '#6AFF45').replace('#', '').toLowerCase();
    const segs: LatLng[][] = [[]];
    for (const p of m.track) {
      if (p === null) { if (segs[segs.length - 1].length) segs.push([]); }
      else segs[segs.length - 1].push(p);
    }
    const overlays = segs
      .filter((s) => s.length >= 2)
      .map((s) => `path-4+${strokeHex}-0.95(${encodeURIComponent(encodePolyline(decimate(s, 200)))})`);
    const [img, field] = await Promise.all([
      loadImage(staticImageUrlForFrame(frame, overlays, 'dark-v11', MAPBOX_TOKEN, true)),
      opts.noWind ? Promise.resolve(null) : fetchWindField(API, jwt, frame.bbox, m.windAtIso),
    ]);
    if (img) ctx.drawImage(img, PAD, mapY, mapW, mapH);
    if (field) {
      const still = renderWindStill(field, frame, 150, 2);
      ctx.globalAlpha = 0.9;
      ctx.drawImage(still, PAD, mapY, mapW, mapH);
      ctx.globalAlpha = 1;
    }
    // Large numbered pins above the wind.
    const R = 26;
    ctx.font = `900 28px ${FONT}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    for (const pin of m.pins) {
      const { x, y } = frame.project(pin.lng, pin.lat);
      const px = PAD + x;
      const py = mapY + y;
      if (px < PAD || px > PAD + mapW || py < mapY || py > mapY + mapH) continue;
      ctx.beginPath();
      ctx.arc(px, py, R + 4, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(10,14,26,0.85)';
      ctx.fill();
      ctx.beginPath();
      ctx.arc(px, py, R, 0, Math.PI * 2);
      ctx.fillStyle = colors.brand;
      ctx.fill();
      ctx.fillStyle = '#0A100C';
      ctx.fillText(pin.label, px, py + 1);
    }
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
  }
  ctx.restore();
  ctx.strokeStyle = colors.border;
  ctx.lineWidth = 3;
  roundedRect(ctx, PAD, mapY, mapW, mapH, 48);
  ctx.stroke();

  // 2×3 stat grid — values shrink to fit their tile.
  const gridY = mapY + mapH + 42;
  const gap = 24;
  const tileW = (innerW - gap * 2) / 3;
  const tileH = 170;
  m.stats.slice(0, 6).forEach((s, i) => {
    const col = i % 3;
    const row = Math.floor(i / 3);
    const x = PAD + col * (tileW + gap);
    const y = gridY + row * (tileH + gap);
    roundedRect(ctx, x, y, tileW, tileH, 36);
    ctx.fillStyle = 'rgba(10, 14, 26, 0.9)';
    ctx.fill();
    ctx.strokeStyle = colors.border;
    ctx.lineWidth = 3;
    ctx.stroke();
    ctx.fillStyle = s.accent ? colors.brand : colors.text;
    ctx.font = fitFont(ctx, s.value, tileW - 48, 900, 54, FONT, 28);
    ctx.fillText(s.value, x + 24, y + 34);
    ctx.fillStyle = colors.textMuted;
    ctx.font = fitFont(ctx, s.label, tileW - 48, 700, 26, MONO, 17);
    ctx.fillText(s.label, x + 24, y + 112);
  });
  const rows = Math.ceil(Math.min(6, m.stats.length) / 3);
  const afterGrid = gridY + rows * tileH + (rows - 1) * gap;

  if (m.footnote) {
    ctx.fillStyle = colors.textMuted;
    ctx.font = `600 32px ${FONT}`;
    wrapLines(ctx, m.footnote, innerW, 2).forEach((line, i) => ctx.fillText(line, PAD, afterGrid + 40 + i * 42));
  }

  const footY = CARD_H - 110;
  ctx.fillStyle = colors.brand;
  ctx.beginPath();
  ctx.arc(PAD + 15, footY + 21, 15, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = colors.text;
  ctx.font = `800 42px ${FONT}`;
  ctx.fillText('aero.chat', PAD + 48, footY);
}

export function canvasToPng(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('render failed'))), 'image/png');
  });
}

export function canWebShareFiles(): boolean {
  try {
    const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean };
    if (typeof nav.share !== 'function' || typeof nav.canShare !== 'function') return false;
    const probe = new File([new Uint8Array([0])], 'aero.png', { type: 'image/png' });
    return nav.canShare({ files: [probe] });
  } catch {
    return false;
  }
}

export async function shareCardFile(blob: Blob, caption: string, filename = 'aero-chat-ride.png'): Promise<void> {
  const file = new File([blob], filename, { type: 'image/png' });
  await navigator.share({ files: [file], text: caption, title: 'aero.chat' });
}

export function downloadBlob(blob: Blob, filename = 'aero-chat-ride.png'): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 2000);
}
